if (!customElements.get('heimat-pack-chooser')) {
  customElements.define('heimat-pack-chooser', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.session = 0;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-pack-content]');
      this.submitButton = this.querySelector('[data-pack-submit]');
      this.submitLabel = this.querySelector('[data-pack-submit-label]');
      this.error = this.querySelector('[data-pack-error]');
      this.cartLink = this.querySelector('[data-pack-cart]');
      this.querySelector('[data-pack-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('pointerdown', event => { this.backdropStart = this.isBackdrop(event); });
      this.dialog.addEventListener('click', event => {
        if (this.backdropStart && this.isBackdrop(event)) this.close();
        this.backdropStart = false;
      });
      this.content.addEventListener('change', event => {
        if (event.target.matches('[data-pack-flavor-select]')) {
          this.switchFlavor(event.target.value);
        } else this.updateSelection();
      });
      this.querySelector('form').addEventListener('submit', event => {
        event.preventDefault();
        const selected = this.content.querySelector('input[name=pack]:checked, input[name=pack][type=hidden]');
        if (!selected || selected.dataset.available === 'false' || selected.disabled || this.pending || this.owner?.busy) return;
        if (selected.dataset.needsOptions === 'true') {
          window.location.assign(selected.dataset.url);
          return;
        }
        this.setPending(true);
        this.owner.queueAdd(selected.value, this, this.session);
      });
    }

    isBackdrop(event) {
      const box = this.dialog.getBoundingClientRect();
      return event.target === this.dialog &&
        (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom);
    }

    show(owner, template) {
      if (this.dialog.open) return;
      this.owner = owner;
      this.session++;
      this.content.replaceChildren(template.content.cloneNode(true));
      this.flavors = [];
      this.flavorPages = new Map();
      const source = owner.closest('.hg-card').querySelector('heimat-card-flavors');
      this.currentProduct = source?.dataset.productId;
      this.flavorRoot = source?.dataset.root || '/';
      const session = this.session;
      if (source?.dataset.productId && source.ensureProducts) {
        source.ensureProducts().then(products => {
          if (this.session !== session || this.owner !== owner) return;
          this.flavors = products;
          this.renderFlavors();
        }).catch(() => { /* Pack selection stays usable without the group service. */ });
      }
      this.setPending(false);
      this.updateSelection();
      this.dialog.showModal();
      (this.content.querySelector('input[name=pack]:checked') || this.querySelector('[data-pack-close]')).focus({ preventScroll: true });
    }

    renderFlavors() {
      this.content.querySelector('[data-pack-flavors]')?.remove();
      if (this.flavors.length < 2) return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'hg-pack__flavors';
      fieldset.dataset.packFlavors = '';
      fieldset.disabled = this.pending;
      const legend = document.createElement('legend');
      legend.textContent = this.dataset.flavorLabel;
      fieldset.append(legend);
      const options = document.createElement('select');
      options.id = 'HeimatPackFlavor';
      options.dataset.packFlavorSelect = '';
      options.setAttribute('aria-label', this.dataset.flavorLabel);
      options.className = 'hg-pack__flavor-select';
      const flavorLabel = product => product.variant_label || product.title;
      for (const product of this.flavors) {
        const id = String(product.id).split('/').pop();
        const button = document.createElement('option');
        button.value = id;
        const label = flavorLabel(product);
        button.textContent = label;
        button.title = product.title;
        button.selected = id === this.currentProduct;
        options.append(button);
      }
      fieldset.append(options);
      this.content.querySelector('.hg-pack__options').before(fieldset);
    }

    async switchFlavor(id) {
      if (id === this.currentProduct || this.pending) return;
      const product = this.flavors.find(item => String(item.id).split('/').pop() === id);
      if (!product) return;
      const session = this.session;
      const packCount = this.content.querySelector('input[name=pack]:checked, input[name=pack][type=hidden]')?.dataset.packCount;
      this.setPending(true);
      this.submitLabel.textContent = this.dataset.flavorLoading;
      try {
        if (!this.flavorPages.has(id)) {
          const url = new URL(`${this.flavorRoot.replace(/\/$/, '')}/products/${encodeURIComponent(product.handle)}`, location.origin);
          url.searchParams.set('section_id', 'heimat-quick-pack');
          this.flavorPages.set(id, fetch(url, { signal: AbortSignal.timeout(10000) }).then(response => {
            if (!response.ok) throw new Error('Pack lookup failed');
            return response.text();
          }));
        }
        const html = await this.flavorPages.get(id);
        if (this.session !== session || !this.dialog.open) return;
        const document = new DOMParser().parseFromString(html, 'text/html');
        const template = document.querySelector('[data-pack-template]');
        if (!template) throw new Error('No purchasable packs');
        this.content.replaceChildren(template.content.cloneNode(true));
        const matching = [...this.content.querySelectorAll('input[name="pack"]')]
          .find(input => packCount && input.dataset.packCount === packCount);
        if (matching) matching.checked = true;
        this.currentProduct = id;
        this.renderFlavors();
        this.setPending(false);
        this.updateSelection();
        this.content.querySelector('[data-pack-flavor-select]')?.focus({ preventScroll: true });
      } catch (error) {
        if (this.session !== session || !this.dialog.open) return;
        this.flavorPages.delete(id);
        this.content.querySelector('[data-pack-flavor-select]').value = this.currentProduct;
        this.setPending(false);
        this.updateSelection();
        this.error.textContent = this.dataset.flavorError;
        this.error.hidden = false;
        this.content.querySelector('[data-pack-flavor-select]')?.focus({ preventScroll: true });
      }
    }

    close() {
      const owner = this.owner;
      this.owner = null;
      this.session++;
      this.dialog.close();
      if (owner?.isConnected) owner.button.focus({ preventScroll: true });
    }

    updateSelection() {
      const selected = this.content.querySelector('input[name=pack]:checked, input[name=pack][type=hidden]');
      if (!selected) return;
      this.error.hidden = true;
      this.error.textContent = '';
      this.cartLink.hidden = true;
      const image = this.content.querySelector('img');
      if (image && selected.dataset.image) {
        image.removeAttribute('srcset');
        image.src = selected.dataset.image;
      }
      this.submitLabel.textContent = selected.dataset.needsOptions === 'true'
        ? this.submitButton.dataset.optionsLabel
        : this.submitButton.dataset.label;
      this.submitButton.disabled = this.pending || selected.dataset.available === 'false';
    }

    setPending(pending) {
      this.pending = pending;
      this.submitButton.disabled = pending || this.content.querySelector('input[name=pack]:checked, input[name=pack][type=hidden]')?.dataset.available === 'false';
      this.submitButton.setAttribute('aria-busy', String(pending));
      this.content.querySelectorAll('fieldset').forEach(fieldset => { fieldset.disabled = pending; });
      this.querySelector('.loading__spinner').classList.toggle('hidden', !pending);
      this.querySelector('[data-pack-arrow]').hidden = pending;
      if (pending) {
        this.error.hidden = true;
        this.cartLink.hidden = true;
        this.submitLabel.textContent = this.submitButton.dataset.loadingLabel;
      }
    }

    succeed(owner, session) {
      // Closing one request must never dismiss a different snack opened while
      // that request was in flight. Dismissing the dialog doesn't cancel an add.
      if (this.owner === owner && this.session === session && this.dialog.open) this.close();
    }

    fail(owner, session, message, checkCart) {
      if (this.owner !== owner || this.session !== session || !this.dialog.open) return;
      this.setPending(false);
      this.updateSelection();
      this.error.textContent = message;
      this.error.hidden = false;
      this.cartLink.hidden = !checkCart;
    }
  });
}
